import React, { useState, useRef, useCallback, useEffect } from 'react';
import './ProductSection.css';
import GotokiButton from '../GotokiButton';
import ProductSlider from '../ProductSlider';
import HeadphoneTypeSelector from '../HeadphoneTypeSelector';
import { useLanguage } from '../../../contexts/LanguageContext';

const ProductSection = ({ onResetRequest }) => {
  const { translate } = useLanguage();
  const [isCallActive, setIsCallActive] = useState(false);
  const buttonRef = useRef(null);
  const userSpokeAfterSelectorRef = useRef(false);

  // State für KI-Empfehlungen
  const [recommendations, setRecommendations] = useState({
    primaryProduct: null,
    secondaryProducts: []
  });

  // Loading state for product recommendation popup (skeleton shimmer)
  const [isProductLoading, setIsProductLoading] = useState(false);

  // State für Headphone Type Selection
  const [headphoneTypes, setHeadphoneTypes] = useState({
    recommendedTypes: [],
    showSelector: false,
    isLoading: false
  });

  // Reset function to clear recommendations and stop widget
  const handleReset = useCallback(() => {
    console.log('🔄 Resetting products and stopping widget');

    // Clear recommendations
    setRecommendations({
      primaryProduct: null,
      secondaryProducts: []
    });
    setIsProductLoading(false);

    // Clear headphone type selector
    setHeadphoneTypes({
      recommendedTypes: [],
      showSelector: false,
      isLoading: false
    });

    // Stop the widget if it's active
    if (isCallActive) {
      // Access the gotoki-button element and trigger its button (same as ProductSlider does)
      const gotokiButton = document.querySelector('gotoki-button');
      if (gotokiButton && gotokiButton.shadowRoot) {
        const innerButton = gotokiButton.shadowRoot.querySelector('button');
        if (innerButton) {
          console.log('🛑 Clicking gotoki-button to stop the call');
          innerButton.click();
        }
      }
    }
  }, [isCallActive]);

  // Expose reset function to parent
  useEffect(() => {
    if (onResetRequest) {
      onResetRequest(handleReset);
    }
  }, [onResetRequest, handleReset]);

  const handleCallStatusChange = useCallback((status) => {
    setIsCallActive(status);
  }, []);

  // Callback für Produktempfehlungen von Gotoki
  const handleProductRecommendation = useCallback((recommendation) => {
    console.log('📦 Received recommendation in ProductSection:', recommendation);
    setIsProductLoading(false);
    setRecommendations({
      primaryProduct: recommendation.primaryProduct,
      secondaryProducts: recommendation.secondaryProducts || []
    });
  }, []);

  // Callback for Product Recommendation Loading - shows the popup with shimmer placeholders
  const handleProductRecommendationLoading = useCallback(() => {
    console.log('📦 Product recommendation loading');
    setRecommendations({ primaryProduct: null, secondaryProducts: [] });
    setIsProductLoading(true);
  }, []);

  // Callback für Headphone Type Selection von Gotoki
  const handleHeadphoneTypeSelection = useCallback((typeData) => {
    console.log('🎧 Received headphone type selection in ProductSection:', typeData);
    userSpokeAfterSelectorRef.current = false;
    setHeadphoneTypes({
      recommendedTypes: typeData.recommendedTypes || [],
      showSelector: true,
      isLoading: false
    });
  }, []);

  // Callback for Headphone Type Loading - opens selector with skeleton placeholders
  const handleHeadphoneTypeLoading = useCallback(() => {
    console.log('🎧 Headphone type selector loading');
    userSpokeAfterSelectorRef.current = false;
    setHeadphoneTypes({
      recommendedTypes: [],
      showSelector: true,
      isLoading: true
    });
  }, []);

  // Callback for Call End Event -
  const handleCallEnd = useCallback((data) => {
    console.log('📞 Call ended:', data.reason, '-', data.message);
    console.log('🔄 Clearing recommendations due to call end');
    setRecommendations({ primaryProduct: null, secondaryProducts: [] });
    setIsProductLoading(false);
    setHeadphoneTypes({ recommendedTypes: [], showSelector: false, isLoading: false });
  }, []);

  // Close headphone type selector
  const handleCloseTypeSelector = useCallback(() => {
    console.log('🎧 Closing headphone type selector');
    setHeadphoneTypes({
      recommendedTypes: [],
      showSelector: false,
      isLoading: false
    });
  }, []);

  // Close headphone type selector after user speaks and bot replies
  const handleMessage = useCallback((message) => {
    // Track when user has spoken after the selector was shown
    if (message.type === 'user_transcript') {
      setHeadphoneTypes((prev) => {
        if (prev.showSelector) {
          userSpokeAfterSelectorRef.current = true;
          console.log('🎧 User spoke while headphone type selector is open');
        }
        return prev;
      });
      return;
    }

    // Close on the next bot response after the user has spoken
    if (message.type === 'bot_transcript' && userSpokeAfterSelectorRef.current) {
      setHeadphoneTypes((prev) => {
        if (prev.showSelector) {
          console.log('🎧 Closing headphone type selector - bot replied after user spoke');
          userSpokeAfterSelectorRef.current = false;
          return { recommendedTypes: [], showSelector: false, isLoading: false };
        }
        return prev;
      });
    }
  }, []);

  return (
    <main className="flex-1 flex flex-col bg-transparent relative overflow-visible min-h-full w-full">
      {/* Main Hero Section - Desktop: 3 columns, Mobile: stacked */}
      <div className="hero-section flex-1 grid grid-cols-1 lg:grid-cols-[1fr_1.2fr_1fr] items-center gap-2 sm:gap-3 md:gap-5 lg:gap-[30px] xl:gap-10 px-3 sm:px-4 md:px-[15px] lg:px-10 xl:px-[60px] py-2 sm:py-[10px] md:py-4 lg:py-5 xl:py-[30px] max-w-[1400px] mx-auto w-full min-h-0">
        {/* Left - Beyerdynamic Logo */}
        <div className="flex justify-center items-center order-1">
          <div className="flex flex-col items-center">
            <img
              src="/Assets/beyerdynamiclogo.png"
              alt="beyerdynamic Logo"
              className="beyerdynamic-logo w-[110px] sm:w-[130px] md:w-[150px] lg:w-[180px] xl:w-[220px] 2xl:w-[260px] h-auto brightness-110 transition-transform duration-300 hover:scale-105"
            />
          </div>
        </div>

        {/* Center - Headphone with Gotoki Button */}
        <div className="flex justify-center items-center order-2">
          <GotokiButton
            ref={buttonRef}
            onCallStatusChange={handleCallStatusChange}
            onProductRecommendation={handleProductRecommendation}
            onProductRecommendationLoading={handleProductRecommendationLoading}
            onHeadphoneTypeSelection={handleHeadphoneTypeSelection}
            onHeadphoneTypeLoading={handleHeadphoneTypeLoading}
            onMessage={handleMessage}
            onCallEnd={handleCallEnd}
          />
        </div>

        {/* Right - Text Content */}
        <div className="flex flex-col gap-2 sm:gap-[10px] md:gap-3 lg:gap-4 text-center items-center order-3">
          <h1 className="main-cta-title font-mont text-[0.85rem] sm:text-base md:text-lg lg:text-[1.4rem] xl:text-[1.6rem] 2xl:text-[1.6rem] font-extrabold text-white uppercase tracking-[1px] sm:tracking-[1.5px] md:tracking-[2px] lg:tracking-[2.5px] xl:tracking-[3px] leading-[1.4] text-center flex flex-col items-center m-0">
            {(() => {
              const titleLines = translate('mainCtaTitle');
              return (
                <>
                  {/* 2 rows on narrow screens (below lg breakpoint) */}
                  <span className="block whitespace-nowrap lg:hidden">{titleLines[0]} {titleLines[1]} {titleLines[2]}</span>
                  {/* 3 rows on wider screens (lg and above) */}
                  <span className="hidden lg:block whitespace-nowrap">{titleLines[0]}</span>
                  <span className="hidden lg:block whitespace-nowrap">{titleLines[1]}</span>
                  <span className="hidden lg:block whitespace-nowrap">{titleLines[2]}</span>
                </>
              );
            })()}
          </h1>
          <p className="main-cta-subtitle font-mont text-[0.75rem] sm:text-sm md:text-base lg:text-[0.95rem] xl:text-[1.05rem] 2xl:text-[1.05rem] text-white/85 font-normal leading-relaxed text-center m-0 max-w-full sm:max-w-[90%] md:max-w-sm lg:max-w-[340px] xl:max-w-[380px] px-0 sm:px-[15px] md:px-[10px] lg:px-0">
            {translate('mainCtaSubtitle')}
          </p>
        </div>
      </div>

      {/* Bottom Product Slider */}
      <ProductSlider
        isCallActive={isCallActive}
        buttonRef={buttonRef}
        primaryRecommendation={recommendations.primaryProduct}
        secondaryRecommendations={recommendations.secondaryProducts}
        isProductLoading={isProductLoading}
      >
        {/* Headphone Type Selector - shown inside ProductSlider area */}
        {headphoneTypes.showSelector && (
          <HeadphoneTypeSelector
            recommendedTypes={headphoneTypes.recommendedTypes}
            isLoading={headphoneTypes.isLoading}
            onClose={handleCloseTypeSelector}
          />
        )}
      </ProductSlider>
    </main>
  );
};

export default ProductSection;

